import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { messagingService } from '../../services/messagingService';
import ContactList from './components/ContactList';
import MessagePanel from './components/MessagePanel';
import NewConversationModal from './components/NewConversationModal';
import { MessageCircle, Users, Plus, Search } from 'lucide-react';

const DirectMessagingInterface = () => {
  const { user, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const subscriptionRef = useRef(null);

  // Load conversations on component mount
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Subscribe to real-time conversation updates
  useEffect(() => {
    if (user) {
      subscriptionRef.current = messagingService?.subscribeToConversations(() => {
        loadConversations();
      });
    }

    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current?.unsubscribe();
      }
    };
  }, [user]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await messagingService?.getConversations();
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      const data = await messagingService?.getMessages(conversationId);
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation?.id);
  };

  const handleSendMessage = async (content, phoneNumber = null) => {
    if (!selectedConversation) return;

    try {
      const newMessage = await messagingService?.sendMessage(
        selectedConversation?.id, 
        content, 
        phoneNumber
      );
      setMessages(prev => [...prev, newMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleNewConversation = async (participantId) => {
    try {
      const result = await messagingService?.createDirectConversation(participantId);
      await loadConversations();
      
      // Select the conversation
      const conversation = conversations?.find(c => c?.id === result?.conversationId);
      if (conversation) {
        handleConversationSelect(conversation);
      }
      
      setShowNewConversation(false);
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError('Failed to create conversation. Please try again.');
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations?.filter(conv => 
    conv?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    conv?.participants?.some(p => 
      p?.user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  );

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Not authenticated</h3>
          <p className="mt-1 text-sm text-gray-500">Please log in to access messaging.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <MessageCircle className="h-8 w-8 text-blue-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Direct Messaging</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-500">
                <Users className="h-4 w-4 mr-1" />
                {userProfile?.fullName || user?.email}
              </div>
              <button
                onClick={() => setShowNewConversation(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Chat
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex h-full">
          {/* Left Sidebar - Contact List */}
          <div className="w-1/3 bg-white rounded-lg shadow-sm border border-gray-200 mr-6">
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <ContactList
              conversations={filteredConversations}
              selectedConversation={selectedConversation}
              onConversationSelect={handleConversationSelect}
              loading={loading}
              error={error}
            />
          </div>

          {/* Right Panel - Messages */}
          <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200">
            {selectedConversation ? (
              <MessagePanel
                conversation={selectedConversation}
                messages={messages}
                onSendMessage={handleSendMessage}
                loading={messagesLoading}
                currentUser={user}
                userProfile={userProfile}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No conversation selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a conversation from the sidebar or start a new one.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setShowNewConversation(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Start New Chat
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* New Conversation Modal */}
      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          onCreateConversation={handleNewConversation}
        />
      )}
    </div>
  );
};

export default DirectMessagingInterface;